import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GameController, Eye, EyeSlash } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { Logo } from '../components/Logo';
import { GoogleSignInButton, AppleSignInButton } from '../components/SocialSignIn';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (username.length < 3 || username.length > 20) {
      setError('Username must be 3-20 characters');
      return;
    }

    setLoading(true);
    const result = await register(email, password, name, username);
    
    if (result.success) {
      navigate('/onboarding', { replace: true });
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="app-container flex flex-col items-center justify-center min-h-screen p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <Logo variant="full" size="lg" clickable={false} showTagline={false} />
          <h1 className="text-xl font-bold text-white mt-5" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Join CyBoard
          </h1>
          <p className="text-[#A1A1AA] mt-1 text-[13px] text-center">Find your gaming tribe</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-3 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm"
              data-testid="register-error"
            >
              {error}
            </motion.div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field"
              placeholder="Your display name"
              required
              data-testid="register-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              className="input-field"
              placeholder="unique_username"
              required
              data-testid="register-username"
            />
            <p className="text-[#71717A] text-xs mt-1">3-20 chars, letters, numbers, _ and - only</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field"
              placeholder="your@email.com"
              required
              data-testid="register-email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pr-12"
                placeholder="••••••••"
                required
                data-testid="register-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white transition-colors"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input-field"
              placeholder="••••••••"
              required
              data-testid="register-confirm-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="register-submit"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Creating account...
              </span>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Social Sign-In */}
        <div className="mt-6">
          <div className="relative flex items-center">
            <div className="flex-1 h-px bg-white/[0.06]"></div>
            <span className="px-3 text-[10px] uppercase tracking-wider text-[#71717A]">or sign up with</span>
            <div className="flex-1 h-px bg-white/[0.06]"></div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 [&>*]:flex-1 [&>*]:min-w-[140px]">
            <GoogleSignInButton />
            <AppleSignInButton />
          </div>
        </div>

        <p className="text-center mt-6 text-[#A1A1AA]">
          Already have an account?{' '}
          <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="login-link">
            Sign In
          </Link>
        </p>

        <p className="text-center mt-4 text-[10px] text-[#555]">
          By creating an account you agree to our{' '}
          <Link to="/terms" className="text-[#777] hover:text-[#9D4CDD] underline" data-testid="terms-link">Terms</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#777] hover:text-[#9D4CDD] underline" data-testid="privacy-link">Privacy Policy</Link>.
        </p>
      </motion.div>
    </div>
  );
}
